import { motion } from 'framer-motion';
import { useState } from 'react';

const ServicesSection = () => {
  const [selectedService, setSelectedService] = useState<number | null>(null);

  const services = [
    {
      id: 1,
      title: '勝つ経営コース',
      subtitle: '本気で夢を実現したい経営者へ',
      description: '本当に真剣に自分の思いを実現したい人のためのコースです。このコースを真剣にやった人は必ず結果を出しています。',
      icon: 'ri-trophy-line',
      gradient: 'from-red-600 via-red-500 to-orange-500',
      features: [
        {
          title: '思いの言語化・数値化',
          desc: 'あなたの実現したいことを明確な言葉と数字に落とし込みます'
        },
        {
          title: '事業の現状把握と仕組み作り',
          desc: '継続的に安定した収入が得られる事業の仕組みを構築します'
        },
        {
          title: '月次フォローアップ',
          desc: '実行内容の確認と次のアクションを一緒に決定していきます'
        },
        {
          title: '人脈紹介・資金繰り支援',
          desc: '事業に必要な方の紹介や金融機関への同行も行います'
        },
        {
          title: '会計・税務の完全サポート',
          desc: '申告代行から事業計画書作成まで包括的にサポート'
        }
      ],
      badge: '最も人気',
      badgeColor: 'bg-red text-white'
    },
    {
      id: 2,
      title: 'お勧めコース',
      subtitle: '業績向上を目指す経営者へ',
      description: '毎月の経営状況を把握し、迅速な意思決定で業績向上を実現するコースです。',
      icon: 'ri-line-chart-line',
      gradient: 'from-blue-600 via-blue-500 to-cyan-500',
      features: [
        {
          title: '月次業績の迅速な把握',
          desc: '経理担当者への指導を通じて、毎月の業績を素早く把握できる体制を構築'
        },
        {
          title: '中間納税の最適化',
          desc: '決算から6ヶ月時点で有利な納税方法を検討。税金を大幅削減した実績多数'
        },
        {
          title: '決算前の税額シミュレーション',
          desc: '事前に納税額を把握し、資金繰りや決算賞与の計画を立てられます'
        },
        {
          title: '金融機関との信頼構築',
          desc: '毎月の試算表提出で銀行の信用が向上。融資が受けやすくなります'
        },
        {
          title: '税務リスクの事前回避',
          desc: '会計上の誤りや税務上の問題を早期発見し、トラブルを未然に防ぎます'
        },
        {
          title: '機動的な経営判断',
          desc: '業績変化に応じた投資やリストラを迅速に実行できる情報を提供'
        }
      ],
      badge: 'おすすめ',
      badgeColor: 'bg-blue-600 text-white'
    },
    {
      id: 3,
      title: 'お手軽コース',
      subtitle: 'コストを抑えたい経営者へ',
      description: '経理はできるが申告書は専門家に任せたいという方向けの、費用を抑えたコースです。',
      icon: 'ri-file-check-line',
      gradient: 'from-green-600 via-green-500 to-emerald-500',
      features: [
        {
          title: '申告書作成に特化',
          desc: '必要事項のみ質問し、効率的に申告書を作成します'
        },
        {
          title: '無駄なサービスを省略',
          desc: '重要性の高い部分に絞り、料金を大幅に抑えることができます'
        },
        {
          title: '赤字企業にも最適',
          desc: '税務調査リスクより費用軽減を優先したい方に向いています'
        }
      ],
      badge: '低価格',
      badgeColor: 'bg-green-600 text-white'
    }
  ];

  const additionalServices = [
    {
      icon: 'ri-global-line',
      title: '国際税務',
      description: '国際税務研究会会員として能力を維持向上。外国税額控除等の国際税務に対応。外国人の方の確定申告や年金の脱退一時金還付申告も実績多数。',
      stats: '2012年：技能研修生64名、2013年：23名の還付申告実績'
    },
    {
      icon: 'ri-stock-line',
      title: '株価計算',
      description: '相続税申告に必要な株価計算のほか、裁判資料としての株価計算も対応。2012年には裁判所提出実績もあります。',
      stats: '相続・裁判資料として高い評価'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'その他専門サービス',
      description: '相続税・贈与税申告、年末調整、償却資産税申告、帳簿復元、不正調査、財務調査、大量保有報告書作成、株主総会議事録作成、記帳代行等',
      stats: '幅広い専門サービスに対応'
    }
  ];

  return (
    <section id="services" className="py-16 sm:py-20 lg:py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-block mb-3 sm:mb-4 px-4 sm:px-5 py-2 border border-red/20 bg-red/5 rounded-full">
            <span className="text-red text-xs sm:text-[13px] font-medium">
              サービス
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            あなたに最適なプランを
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            事業規模や目標に応じて、3つのコースからお選びいただけます
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 hover:shadow-xl transition-all hover:-translate-y-1"
            >
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-red/10 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                <i className={`${service.icon} text-2xl sm:text-3xl text-red`}></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {service.title}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                {service.description}
              </p>
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 sm:gap-3 text-sm sm:text-base text-gray-700">
                    <i className="ri-check-line text-red text-lg sm:text-xl flex-shrink-0"></i>
                    <span>{feature.title}</span>
                  </li>
                ))}
              </ul>
              <a
                href="/services"
                className="block w-full bg-red text-white px-6 py-3 rounded-lg hover:bg-red-dark transition-colors text-sm sm:text-[15px] font-medium text-center whitespace-nowrap cursor-pointer"
              >
                詳しく見る
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
